import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Briefcase, TrendingUp, RefreshCw, Repeat, Plus, Save, Mail } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import DashboardLayout, { StatusBadge, STATUS_STAGES } from "../../components/layout/DashboardLayout";
import AuthImage from "../../components/AuthImage";
import api, { formatApiError } from "../../lib/api";

const PACKAGE_PRICES = { starter: 497, professional: 997, premium: 1497, refresh: 697 };

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  return (
    <DashboardLayout title="Admin Dashboard" actions={<Button variant="outline" className="rounded-full" onClick={loadStats}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>}>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="admin-stats">
        <Stat icon={Users} label="Leads" value={stats?.leads} />
        <Stat icon={Users} label="Clients" value={stats?.clients} />
        <Stat icon={Briefcase} label="Active Projects" value={stats?.active_projects} />
        <Stat icon={Repeat} label="Awaiting Review" value={stats?.awaiting_review} />
        <Stat icon={TrendingUp} label="Revenue" value={stats != null ? `$${stats.revenue}` : undefined} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-[#1b1b1d] p-1 rounded-full w-fit border border-white/10">
          {["overview", "leads", "clients", "projects", "revisions", "payments", "portfolio", "content"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="rounded-full capitalize font-medium text-[#D2CAB2] hover:text-[#F3EDDC] data-[state=active]:bg-[#C9A227] data-[state=active]:text-black data-[state=active]:shadow-none">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview"><Overview stats={stats} /></TabsContent>
        <TabsContent value="leads"><Leads /></TabsContent>
        <TabsContent value="clients"><Clients /></TabsContent>
        <TabsContent value="projects"><Projects onChange={loadStats} /></TabsContent>
        <TabsContent value="revisions"><Revisions onChange={loadStats} /></TabsContent>
        <TabsContent value="payments"><Payments /></TabsContent>
        <TabsContent value="portfolio"><Portfolio /></TabsContent>
        <TabsContent value="content"><Content /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#948C79]">{label}</span>
        <Icon className="h-4 w-4 text-[#C9A227]" />
      </div>
      <div className="mt-2 font-display text-2xl font-bold text-[#F3EDDC]">{value ?? "—"}</div>
    </Card>
  );
}

function Overview({ stats }) {
  return (
    <Card className="mt-6 p-6">
      <h3 className="font-display font-semibold text-[#F3EDDC]">Business overview</h3>
      <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
        <div className="rounded-xl bg-[#0c0c0d] p-4"><div className="text-[#948C79]">Total projects</div><div className="font-display text-xl font-bold">{stats?.projects ?? "—"}</div></div>
        <div className="rounded-xl bg-[#0c0c0d] p-4"><div className="text-[#948C79]">Pending revisions</div><div className="font-display text-xl font-bold">{stats?.pending_revisions ?? "—"}</div></div>
        <div className="rounded-xl bg-[#0c0c0d] p-4"><div className="text-[#948C79]">Revenue collected</div><div className="font-display text-xl font-bold">${stats?.revenue ?? "—"}</div></div>
      </div>
    </Card>
  );
}

function Leads() {
  const [leads, setLeads] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.patch(`/leads/${edit.id}`, { status: edit.status, notes: edit.notes, next_action: edit.next_action, last_contact: edit.last_contact });
      toast.success("Lead updated"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="mt-6 overflow-hidden" data-testid="admin-leads">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Business</TableHead><TableHead>Contact</TableHead>
            <TableHead>Industry</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead><TableHead>Next action</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {leads.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-[#7E7663] py-8">No leads yet.</TableCell></TableRow>}
            {leads.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell className="font-medium">{l.name}</TableCell>
                <TableCell>{l.business || "—"}</TableCell>
                <TableCell className="text-sm"><div>{l.email}</div><div className="text-[#7E7663]">{l.phone}</div></TableCell>
                <TableCell>{l.industry || "—"}</TableCell>
                <TableCell><span className="text-xs bg-[#1b1b1d] rounded-full px-2 py-0.5">{l.lead_source}</span></TableCell>
                <TableCell><StatusBadge status={l.status} /></TableCell>
                <TableCell className="text-sm text-[#948C79]">{l.next_action || "—"}</TableCell>
                <TableCell><Button size="sm" variant="ghost" data-testid={`lead-edit-${l.id}`} onClick={() => setEdit({ ...l })}>Manage</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manage lead</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div><Label>Status</Label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Lead", "Contacted", "Interested", "Proposal", "Paid"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Next action</Label><Input value={edit.next_action || ""} onChange={(e) => setEdit({ ...edit, next_action: e.target.value })} /></div>
              <div><Label>Last contact</Label><Input value={edit.last_contact || ""} onChange={(e) => setEdit({ ...edit, last_contact: e.target.value })} placeholder="e.g. 2026-06-01" /></div>
              <div><Label>Notes</Label><Textarea value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={3} /></div>
            </div>
          )}
          <DialogFooter><Button data-testid="lead-save-btn" onClick={save} className="rounded-full bg-[#C9A227] text-black"><Save className="h-4 w-4 mr-1" /> Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Clients() {
  const [clients, setClients] = useState([]);
  useEffect(() => { api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  return (
    <Card className="mt-6 overflow-x-auto" data-testid="admin-clients">
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Business</TableHead><TableHead>Projects</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
        <TableBody>
          {clients.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#7E7663] py-8">No clients yet.</TableCell></TableRow>}
          {clients.map((c) => (
            <TableRow key={c.user_id}><TableCell className="font-medium">{c.name}</TableCell><TableCell>{c.email}</TableCell><TableCell>{c.business_name || "—"}</TableCell><TableCell>{c.project_count}</TableCell><TableCell className="text-[#7E7663] text-sm">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function Projects({ onChange }) {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(null);
  const [assets, setAssets] = useState(null);
  const [brief, setBrief] = useState(null);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const saveEdit = async () => {
    try {
      await api.patch(`/projects/${edit.id}`, { status: edit.status, preview_url: edit.preview_url, package: edit.package, price: parseFloat(edit.price) });
      toast.success("Project updated"); setEdit(null); load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doCreate = async () => {
    if (!create.client_id || !create.name) { toast.error("Client and name required"); return; }
    try {
      await api.post("/projects", { client_id: create.client_id, name: create.name, package: create.package, price: PACKAGE_PRICES[create.package], status: create.status });
      toast.success("Project created"); setCreate(null); load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="mt-6" data-testid="admin-projects">
      <div className="p-4 flex justify-end">
        <Button data-testid="create-project-btn" onClick={() => setCreate({ client_id: "", name: "", package: "starter", status: "Paid" })} className="rounded-full bg-[#C9A227] text-black"><Plus className="h-4 w-4 mr-1" /> New project</Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Project</TableHead><TableHead>Client</TableHead><TableHead>Package</TableHead><TableHead>Price</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {projects.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#7E7663] py-8">No projects yet.</TableCell></TableRow>}
            {projects.map((p) => (
              <TableRow key={p.id} data-testid={`project-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-sm">{p.client?.name}<div className="text-[#7E7663]">{p.client?.email}</div></TableCell>
                <TableCell className="capitalize">{p.package}</TableCell>
                <TableCell>${p.price}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell><Button size="sm" variant="ghost" data-testid={`project-edit-${p.id}`} onClick={() => { setEdit({ ...p }); setAssets(null); setBrief(null); api.get(`/onboarding/${p.id}`).then((r) => setAssets(r.data)).catch(() => setAssets(null)); api.get(`/brief/${p.id}`).then((r) => setBrief(r.data)).catch(() => setBrief(null)); }}>Manage</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manage project</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div><Label>Status</Label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Preview URL</Label><Input data-testid="project-preview-url" value={edit.preview_url || ""} onChange={(e) => setEdit({ ...edit, preview_url: e.target.value })} placeholder="https://" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Package</Label>
                  <Select value={edit.package} onValueChange={(v) => setEdit({ ...edit, package: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["starter", "professional", "premium", "refresh"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Price</Label><Input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} /></div>
              </div>
              {assets && (assets.logo_url || (assets.photos || []).length > 0) && (
                <div data-testid="admin-onboarding-assets">
                  <Label>Client brand assets</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {assets.logo_url && <AuthImage path={assets.logo_url} className="h-16 w-16 rounded-lg object-cover border border-white/10" />}
                    {(assets.photos || []).map((ph) => <AuthImage key={ph} path={ph} className="h-16 w-16 rounded-lg object-cover border border-white/10" />)}
                  </div>
                </div>
              )}
              {assets && (
                <details data-testid="admin-onboarding-details" className="rounded-lg border border-white/10 p-3">
                  <summary className="cursor-pointer text-sm text-[#C9A227]">Client answers — what they want built</summary>
                  <div className="mt-2 space-y-1.5 text-sm">
                    {[["Business", assets.business_name], ["Description", assets.business_description], ["Services", assets.services], ["Service areas", assets.service_areas], ["Phone", assets.phone], ["Email", assets.email], ["Address", assets.address], ["Hours", assets.business_hours], ["Social", assets.social_links], ["Brand colors", assets.brand_colors], ["Competitors", assets.competitor_websites], ["Likes", assets.preferred_examples], ["Primary action", assets.desired_action]].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}><span className="text-[#7E7663]">{k}: </span><span className="text-[#D2CAB2]">{v}</span></div>
                    ))}
                  </div>
                </details>
              )}
              {brief?.data && (
                <details data-testid="admin-brief-view" className="rounded-lg border border-white/10 p-3">
                  <summary className="cursor-pointer text-sm text-[#C9A227]">AI Website Brief{brief?.pdf_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${brief.pdf_url}`} target="_blank" rel="noreferrer" data-testid="admin-brief-pdf" className="ml-3 underline text-xs">Download PDF</a>}</summary>
                  <div className="mt-2 space-y-1.5 text-sm">
                    {Object.entries(brief.data).map(([k, v]) => (
                      <div key={k}><span className="text-[#7E7663]">{k.replace(/_/g, " ")}: </span><span className="text-[#D2CAB2]">{Array.isArray(v) ? v.join(", ") : String(v)}</span></div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          <DialogFooter><Button data-testid="project-save-btn" onClick={saveEdit} className="rounded-full bg-[#C9A227] text-black"><Save className="h-4 w-4 mr-1" /> Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create */}
      <Dialog open={!!create} onOpenChange={(o) => !o && setCreate(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
          {create && (
            <div className="space-y-4">
              <div><Label>Client</Label>
                <Select value={create.client_id} onValueChange={(v) => setCreate({ ...create, client_id: v })}>
                  <SelectTrigger data-testid="create-project-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name} ({c.email})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Project name</Label><Input data-testid="create-project-name" value={create.name} onChange={(e) => setCreate({ ...create, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Package</Label>
                  <Select value={create.package} onValueChange={(v) => setCreate({ ...create, package: v })}>
                    <SelectTrigger data-testid="create-project-package"><SelectValue /></SelectTrigger>
                    <SelectContent>{["starter", "professional", "premium", "refresh"].map((s) => <SelectItem key={s} value={s}>{s} (${PACKAGE_PRICES[s]})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={create.status} onValueChange={(v) => setCreate({ ...create, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="create-project-submit" onClick={doCreate} className="rounded-full bg-[#C9A227] text-black">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Revisions({ onChange }) {
  const [revs, setRevs] = useState([]);
  const load = () => api.get("/revisions").then((r) => setRevs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.patch(`/revisions/${id}`, { status }); toast.success("Updated"); load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card className="mt-6 overflow-x-auto" data-testid="admin-revisions">
      <Table>
        <TableHeader><TableRow><TableHead>Project</TableHead><TableHead>Request</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
        <TableBody>
          {revs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#7E7663] py-8">No revision requests.</TableCell></TableRow>}
          {revs.map((r) => (
            <TableRow key={r.id} data-testid={`revision-row-${r.id}`}>
              <TableCell className="font-medium">{r.project_name || "—"}</TableCell>
              <TableCell className="max-w-md text-sm text-[#B7AF98]">{r.description}</TableCell>
              <TableCell><span className="rounded-full bg-[#1b1b1d] px-2.5 py-1 text-xs font-medium capitalize">{r.status}</span></TableCell>
              <TableCell>
                <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                  <SelectTrigger data-testid={`revision-status-${r.id}`} className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>{["pending", "in_progress", "completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function Payments() {
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);
  return (
    <div className="mt-6 space-y-6" data-testid="admin-payments">
      <Card className="overflow-x-auto">
        <div className="p-4 font-display font-semibold">Orders</div>
        <Table>
          <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Package</TableHead><TableHead>Amount</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#7E7663] py-8">No orders yet.</TableCell></TableRow>}
            {orders.map((o) => (
              <TableRow key={o.order_id} data-testid={`order-row-${o.order_id}`}>
                <TableCell className="font-mono text-xs">{o.order_id}</TableCell>
                <TableCell>{o.package_name}</TableCell>
                <TableCell>${o.amount}</TableCell>
                <TableCell><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${o.payment_status === "PAID" ? "bg-emerald-100 text-emerald-700" : "bg-[#1b1b1d] text-[#B7AF98]"}`}>{o.payment_status}</span></TableCell>
                <TableCell><span className="rounded-full bg-[#1b1b1d] px-2.5 py-1 text-xs font-medium">{o.status}</span></TableCell>
                <TableCell className="text-[#7E7663] text-sm">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Card className="overflow-x-auto">
        <div className="p-4 font-display font-semibold">Payment transactions</div>
        <Table>
          <TableHeader><TableRow><TableHead>Package</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {payments.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#7E7663] py-8">No payments yet.</TableCell></TableRow>}
            {payments.map((p) => (
              <TableRow key={p.session_id}><TableCell>{p.package_name}</TableCell><TableCell>${p.amount}</TableCell><TableCell><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-[#1b1b1d] text-[#B7AF98]"}`}>{p.payment_status}</span></TableCell><TableCell className="text-[#7E7663] text-sm">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function Portfolio() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/portfolio/all").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (item) => {
    try { await api.post("/portfolio", { ...item, published: !item.published }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card className="mt-6 overflow-x-auto" data-testid="admin-portfolio">
      <Table>
        <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Industry</TableHead><TableHead>Slug</TableHead><TableHead>Published</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.slug}><TableCell className="font-medium">{it.title}</TableCell><TableCell>{it.industry}</TableCell><TableCell className="text-[#7E7663]">{it.slug}</TableCell><TableCell><Switch data-testid={`portfolio-toggle-${it.slug}`} checked={it.published} onCheckedChange={() => toggle(it)} /></TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function Content() {
  const [content, setContent] = useState(null);
  const [seoList, setSeoList] = useState([]);
  const [seoEdit, setSeoEdit] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/seo").then((r) => setSeoList(r.data)).catch(() => {});
  }, []);

  const saveContent = async () => {
    try { await api.patch("/content", { data: content }); toast.success("Content saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveSeo = async () => {
    try { await api.post("/seo", seoEdit); toast.success("SEO saved"); setSeoEdit(null); api.get("/seo").then((r) => setSeoList(r.data)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!content) return <Card className="mt-6 p-6 text-[#7E7663]">Loading…</Card>;

  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="admin-content">
      <Card className="p-6">
        <h3 className="font-display font-semibold text-[#F3EDDC] mb-4">Site content</h3>
        <div className="space-y-3">
          {Object.entries(content).map(([k, v]) => (
            <div key={k}><Label className="text-xs uppercase tracking-wide text-[#7E7663]">{k.replace(/_/g, " ")}</Label>
              {String(v).length > 60
                ? <Textarea data-testid={`content-${k}`} value={v} onChange={(e) => setContent({ ...content, [k]: e.target.value })} rows={2} />
                : <Input data-testid={`content-${k}`} value={v} onChange={(e) => setContent({ ...content, [k]: e.target.value })} />}
            </div>
          ))}
        </div>
        <Button data-testid="content-save-btn" onClick={saveContent} className="mt-4 rounded-full bg-[#C9A227] text-black"><Save className="h-4 w-4 mr-1" /> Save content</Button>
      </Card>

      <Card className="p-6">
        <h3 className="font-display font-semibold text-[#F3EDDC] mb-4">SEO settings</h3>
        <div className="space-y-2">
          {seoList.map((s) => (
            <div key={s.page} className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2">
              <div><div className="font-medium capitalize text-sm">{s.page}</div><div className="text-xs text-[#7E7663] truncate max-w-xs">{s.title}</div></div>
              <Button size="sm" variant="ghost" data-testid={`seo-edit-${s.page}`} onClick={() => setSeoEdit({ ...s })}>Edit</Button>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!seoEdit} onOpenChange={(o) => !o && setSeoEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit SEO — {seoEdit?.page}</DialogTitle></DialogHeader>
          {seoEdit && (
            <div className="space-y-3">
              <div><Label>Title</Label><Input data-testid="seo-title" value={seoEdit.title || ""} onChange={(e) => setSeoEdit({ ...seoEdit, title: e.target.value })} /></div>
              <div><Label>Meta description</Label><Textarea data-testid="seo-desc" value={seoEdit.meta_description || ""} onChange={(e) => setSeoEdit({ ...seoEdit, meta_description: e.target.value })} rows={2} /></div>
              <div><Label>OG title</Label><Input value={seoEdit.og_title || ""} onChange={(e) => setSeoEdit({ ...seoEdit, og_title: e.target.value })} /></div>
              <div><Label>Canonical</Label><Input value={seoEdit.canonical || ""} onChange={(e) => setSeoEdit({ ...seoEdit, canonical: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button data-testid="seo-save-btn" onClick={saveSeo} className="rounded-full bg-[#C9A227] text-black">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
